import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateToIso } from "@/lib/format";
import { toast } from "sonner";

const CURRENCIES = ["AUD", "USD", "CAD", "GBP", "EUR", "NZD", "CHF", "JPY", "SGD", "Other"];
const STYLES = ["Classic", "Modern", "Romantic", "Minimal", "Garden", "Luxury", "Boho", "Beach", "Rustic", "Destination", "Other"];
const PALETTE = ["#C5A880", "#8A9A86", "#E8D8D0", "#D4C4B7", "#2C352D", "#FAFAF8", "#B0413E", "#3E5C76", "#EAB595", "#6B8E9E", "#9CAF88", "#2A2A2A"];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    partner1: "", partner2: "", date: "", location: "", venue: "",
    currency: "AUD", budget: "", guest_estimate: "", style: "", colors: [],
  });
  const navigate = useNavigate();
  const { setHasWedding } = useAuth();
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const steps = [
    { title: "Who's getting married?", desc: "Add both partner names. Use whatever feels right — no titles required.", fields: (
      <div className="space-y-4">
        <div><Label className="text-xs text-muted-foreground">Partner 1</Label><Input value={form.partner1} onChange={(e) => set("partner1", e.target.value)} className="rounded-xl h-11 mt-1" data-testid="ob-partner1" autoFocus /></div>
        <div><Label className="text-xs text-muted-foreground">Partner 2</Label><Input value={form.partner2} onChange={(e) => set("partner2", e.target.value)} className="rounded-xl h-11 mt-1" data-testid="ob-partner2" /></div>
      </div>
    )},
    { title: "When's the big day?", desc: "Pick your wedding date. You can change it anytime.", fields: (
      <div><Label className="text-xs text-muted-foreground">Wedding date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="rounded-xl h-11 mt-1" data-testid="ob-date" /></div>
    )},
    { title: "Where's it happening?", desc: "Your city or region.", fields: (
      <div><Label className="text-xs text-muted-foreground">Wedding location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Byron Bay, NSW" className="rounded-xl h-11 mt-1" data-testid="ob-location" /></div>
    )},
    { title: "Do you have a venue?", desc: "Add it now or leave blank for later.", fields: (
      <div><Label className="text-xs text-muted-foreground">Venue</Label><Input value={form.venue} onChange={(e) => set("venue", e.target.value)} className="rounded-xl h-11 mt-1" data-testid="ob-venue" /></div>
    )},
    { title: "Which currency?", desc: "This is used across your budget and expenses.", fields: (
      <div><Label className="text-xs text-muted-foreground">Currency</Label>
        <Select value={form.currency} onValueChange={(v) => set("currency", v)}><SelectTrigger className="rounded-xl h-11 mt-1" data-testid="ob-currency"><SelectValue /></SelectTrigger>
          <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </div>
    )},
    { title: "Estimated budget", desc: "A ballpark is fine — refine it later.", fields: (
      <div><Label className="text-xs text-muted-foreground">Total budget ({form.currency})</Label><Input type="number" value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="35000" className="rounded-xl h-11 mt-1" data-testid="ob-budget" /></div>
    )},
    { title: "How many guests?", desc: "Roughly how many people you expect.", fields: (
      <div><Label className="text-xs text-muted-foreground">Estimated guest count</Label><Input type="number" value={form.guest_estimate} onChange={(e) => set("guest_estimate", e.target.value)} placeholder="100" className="rounded-xl h-11 mt-1" data-testid="ob-guests" /></div>
    )},
    { title: "Your wedding style", desc: "Pick the vibe that fits you best.", fields: (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {STYLES.map((s) => (
          <button key={s} onClick={() => set("style", s)} data-testid={`ob-style-${s}`}
            className={`rounded-xl border px-3 py-3 text-sm transition-colors ${form.style === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{s}</button>
        ))}
      </div>
    )},
    { title: "Choose your palette", desc: "Select the colours for your day.", fields: (
      <div className="grid grid-cols-6 gap-3">
        {PALETTE.map((c) => {
          const active = form.colors.includes(c);
          return (
            <button key={c} onClick={() => set("colors", active ? form.colors.filter((x) => x !== c) : [...form.colors, c])}
              data-testid={`ob-color-${c}`}
              className={`h-12 rounded-xl border-2 relative transition-transform hover:scale-105 ${active ? "border-foreground" : "border-transparent"}`} style={{ background: c }}>
              {active && <Check className="h-4 w-4 absolute inset-0 m-auto" style={{ color: c === "#FAFAF8" ? "#000" : "#fff" }} />}
            </button>
          );
        })}
      </div>
    )},
  ];

  const canNext = () => {
    if (step === 0) return form.partner1.trim() && form.partner2.trim();
    if (step === 1) return !!form.date;
    return true;
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/wedding", {
        ...form,
        budget: Number(form.budget) || 0,
        guest_estimate: Number(form.guest_estimate) || 0,
        date: form.date ? dateToIso(form.date) : null,
      });
      setHasWedding(true);
      toast.success("Your planner is ready");
      navigate("/app");
    } catch { toast.error("Couldn't create your planner. Please try again."); setSaving(false); }
  };

  const cur = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="px-6 sm:px-10 py-6 flex items-center gap-2.5 border-b border-border">
        <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center"><Heart className="h-4 w-4 text-primary-foreground" fill="currentColor" /></div>
        <span className="font-serif text-lg font-semibold text-primary">PlanMyWedding</span>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-8">
            <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
          </div>
          {step === 0 && <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">Welcome to PlanMyWedding</p>}
          <h1 className="text-4xl tracking-tight text-primary leading-tight">{cur.title}</h1>
          <p className="text-muted-foreground mt-2 mb-8">{cur.desc}</p>

          {cur.fields}

          <div className="flex items-center justify-between mt-10">
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="rounded-full" data-testid="ob-back">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
            {step < steps.length - 1 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="rounded-full px-6" data-testid="ob-next">Continue <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : (
              <Button onClick={finish} disabled={saving} className="rounded-full px-6" data-testid="ob-finish">{saving ? "Creating…" : "Create my planner"}</Button>
            )}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">Step {step + 1} of {steps.length}</p>
        </div>
      </div>
    </div>
  );
}
